/**
 * Core API Client for So Han AI Terminal Platform
 * Handles proxy routing, Bearer token injection, SSE streaming, and structured error handling.
 */

export class ApiError extends Error {
  constructor(message, status, data = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export async function apiRequest(endpoint, options = {}) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('titan_auth_token') : null;
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let response;
  try {
    response = await fetch(endpoint, {
      ...options,
      headers,
    });
  } catch (netErr) {
    throw new ApiError(netErr.message || 'Lỗi mạng khi kết nối đến máy chủ bảo mật.', 0);
  }

  // Parse JSON response body if available
  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  // Handle Rate Limiter HTTP 429
  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After') || data.retryAfter || 60;
    const msg = data.message || data.error || `Tần suất yêu cầu vượt giới hạn (Anti-DDoS). Vui lòng thử lại sau ${retryAfter}s.`;
    throw new ApiError(msg, 429, { ...data, retryAfter });
  }

  // Handle Brute-Force Quarantine or Forbidden HTTP 403
  if (response.status === 403) {
    const msg = data.message || data.error || 'Quyền truy cập bị từ chối: Tường lửa đang cách ly IP/Tài khoản do nghi vấn brute-force.';
    throw new ApiError(msg, 403, data);
  }

  // Handle Unauthorized HTTP 401
  if (response.status === 401) {
    const msg = data.message || data.error || 'Phiên làm việc hết hạn hoặc thông tin đăng nhập không hợp lệ.';
    throw new ApiError(msg, 401, data);
  }

  if (!response.ok) {
    const msg = data.message || data.error || `HTTP ${response.status}: Yêu cầu thất bại`;
    throw new ApiError(msg, response.status, data);
  }

  return data;
}

export const authService = {
  login: async (username, password) => {
    return apiRequest('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },
  logout: async () => {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } catch {
      // Ignore network errors on logout
    } finally {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('titan_auth_token');
        localStorage.removeItem('titan_auth_user');
      }
    }
  },
  me: async () => {
    return apiRequest('/api/auth/me', { method: 'GET' });
  },
};

export const marketService = {
  getSignals: async (timeframe = '15m', symbols = 'BTC,ETH,SOL,BNB') => {
    return apiRequest(`/api/market/signals?timeframe=${encodeURIComponent(timeframe)}&symbols=${encodeURIComponent(symbols)}`);
  },
  getRadar: async () => {
    return apiRequest('/api/market/radar');
  },
  getKlines: async (symbol = 'XAUUSD', interval = '1h', limit = 60) => {
    return apiRequest(`/api/market/klines?symbol=${encodeURIComponent(symbol)}&interval=${encodeURIComponent(interval)}&limit=${limit}`);
  },
  analyzeMarket: async (symbol = 'XAUUSD', timeframe = '1h', mode = 'smart', image = null) => {
    return apiRequest('/api/market/analyze', {
      method: 'POST',
      body: JSON.stringify(image ? { symbol, timeframe, mode, image } : { symbol, timeframe, mode })
    });
  },
};


export const aiService = {
  getHubStatus: async () => {
    return apiRequest('/api/ai/hub/status');
  },
  switchProvider: async (provider, config = {}) => {
    return apiRequest('/api/ai/hub/switch', {
      method: 'POST',
      body: JSON.stringify({ provider, config }),
    });
  },
  /**
   * SSE Stream consumer for AI Copilot Chat
   */
  streamChat: async ({ query, symbol = 'BTC/USDT', timeframe = '15m', messages = [], onChunk, onDone, onError, signal }) => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('titan_auth_token') : null;

    try {
      const response = await fetch('/api/ai/copilot/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          query,
          symbol,
          timeframe,
          messages,
        }),
        signal,
      });

      if (!response.ok) {
        let errData = {};
        try {
          errData = await response.json();
        } catch {}
        const msg = errData.message || errData.error || `HTTP ${response.status}: Chat stream error`;
        throw new ApiError(msg, response.status, errData);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;
          const dataStr = trimmed.replace(/^data:\s*/, '');
          if (dataStr === '[DONE]') {
            onDone?.();
            return;
          }

          try {
            const parsed = JSON.parse(dataStr);
            onChunk?.(parsed);
          } catch {
            // ignore non-json line
          }
        }
      }

      onDone?.();
    } catch (err) {
      if (err.name === 'AbortError') return;
      onError?.(err);
    }
  },
};

export const adminService = {
  getUsers: async () => {
    return apiRequest('/api/admin/users');
  },
  createUser: async (userData) => {
    return apiRequest('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },
  suspendUser: async (id) => {
    return apiRequest(`/api/admin/users/${id}/suspend`, {
      method: 'POST',
    });
  },
  activateUser: async (id) => {
    return apiRequest(`/api/admin/users/${id}/activate`, {
      method: 'POST',
    });
  },
  extendLicense: async (id, additionalDays = 30) => {
    return apiRequest(`/api/admin/users/${id}/extend`, {
      method: 'POST',
      body: JSON.stringify({ additionalDays }),
    });
  },
  deleteUser: async (id) => {
    return apiRequest(`/api/admin/users/${id}`, {
      method: 'DELETE',
    });
  },
  getAIHub: async () => {
    return apiRequest('/api/admin/ai-hub');
  },
  updateAIHub: async (provider, config = {}) => {
    return apiRequest('/api/admin/ai-hub', {
      method: 'POST',
      body: JSON.stringify({ provider, config }),
    });
  },
};
